export type PromotionCopy = {
  languageSelector: string
  participation: string
  howToParticipate: string
  addGameStepTitle: string
  addGameStepBody: string
  shareStepTitle: string
  shareStepBody: (hashtag: string) => string
  enteredStepTitle: string
  enteredStepBody: string
  manualEntryStepTitle: string
  manualEntryStepBody: string
  submissionWindow: (start: string, end: string) => string
  automaticDiscovery: (hashtag: string, interval: number) => string
  manualFallback: string
  tweetUrl: string
  submitTweet: string
  submitted: string
  signInPrompt: string
  signIn: string
  submittedTweets: string
  rules: string
}

const copies: Record<string, PromotionCopy> = {
  en: {
    languageSelector: "Language",
    participation: "Participation",
    howToParticipate: "How to participate",
    addGameStepTitle: "Add your games to ChessDrop.com",
    addGameStepBody: "Enter each game after you play it.",
    shareStepTitle: "Share on X",
    shareStepBody: (hashtag) =>
      `Share from ChessDrop and choose this promotion. We add @ChessDrop, ${hashtag}, and your game link.`,
    enteredStepTitle: "Done!",
    enteredStepBody: "We detect qualifying public posts automatically.",
    manualEntryStepTitle: "Add your X link",
    manualEntryStepBody:
      "Return to this page and add the public X post link below to complete your entry.",
    submissionWindow: (start, end) =>
      `The submission window runs from ${start} to ${end}.`,
    automaticDiscovery: (_hashtag, interval) =>
      `Qualifying X posts are checked every ${interval} minutes.`,
    manualFallback: "Missing yours? Submit its link below.",
    tweetUrl: "URL of your X post",
    submitTweet: "Submit post",
    submitted: "Your post was added.",
    signInPrompt: "Sign in to submit a missing post manually.",
    signIn: "Sign in",
    submittedTweets: "Submitted posts",
    rules: "Promotion rules",
  },
  es: {
    languageSelector: "Idioma",
    participation: "Participación",
    howToParticipate: "Cómo participar",
    addGameStepTitle: "Añade tus partidas a ChessDrop.com",
    addGameStepBody: "Introduce cada partida después de jugarla.",
    shareStepTitle: "Comparte en X",
    shareStepBody: (hashtag) =>
      `Comparte desde ChessDrop y elige esta promoción. Añadimos @ChessDrop, ${hashtag} y el enlace a tu partida.`,
    enteredStepTitle: "¡Listo!",
    enteredStepBody:
      "Detectamos automáticamente las publicaciones públicas válidas.",
    manualEntryStepTitle: "Añade el enlace de X",
    manualEntryStepBody:
      "Vuelve a esta página y añade abajo el enlace de la publicación pública para completar tu participación.",
    submissionWindow: (start, end) =>
      `El plazo de participación va del ${start} al ${end}.`,
    automaticDiscovery: (_hashtag, interval) =>
      `Las publicaciones válidas en X se comprueban cada ${interval} minutos.`,
    manualFallback: "¿No aparece la tuya? Envía su enlace abajo.",
    tweetUrl: "URL de tu publicación en X",
    submitTweet: "Enviar publicación",
    submitted: "Tu publicación se ha añadido.",
    signInPrompt:
      "Inicia sesión para enviar manualmente una publicación que falte.",
    signIn: "Iniciar sesión",
    submittedTweets: "Publicaciones registradas",
    rules: "Bases de la promoción",
  },
  fr: {
    languageSelector: "Langue",
    participation: "Participation",
    howToParticipate: "Comment participer",
    addGameStepTitle: "Ajoutez vos parties à ChessDrop.com",
    addGameStepBody: "Saisissez chaque partie après l'avoir jouée.",
    shareStepTitle: "Partagez sur X",
    shareStepBody: (hashtag) =>
      `Partagez depuis ChessDrop et choisissez cette promotion. Nous ajoutons @ChessDrop, ${hashtag} et le lien vers votre partie.`,
    enteredStepTitle: "C'est fait !",
    enteredStepBody:
      "Nous détectons automatiquement les publications publiques valides.",
    manualEntryStepTitle: "Ajoutez le lien X",
    manualEntryStepBody:
      "Revenez sur cette page et ajoutez ci-dessous le lien de la publication publique pour valider votre participation.",
    submissionWindow: (start, end) =>
      `La période de participation s'étend du ${start} au ${end}.`,
    automaticDiscovery: (_hashtag, interval) =>
      `Les publications X valides sont vérifiées toutes les ${interval} minutes.`,
    manualFallback: "La vôtre n'apparaît pas ? Envoyez son lien ci-dessous.",
    tweetUrl: "URL de votre publication X",
    submitTweet: "Envoyer la publication",
    submitted: "Votre publication a été ajoutée.",
    signInPrompt:
      "Connectez-vous pour envoyer manuellement une publication manquante.",
    signIn: "Se connecter",
    submittedTweets: "Publications enregistrées",
    rules: "Règlement de la promotion",
  },
  ca: {
    languageSelector: "Idioma",
    participation: "Participació",
    howToParticipate: "Com participar",
    addGameStepTitle: "Afegeix les teves partides a ChessDrop.com",
    addGameStepBody: "Introdueix cada partida després de jugar-la.",
    shareStepTitle: "Comparteix a X",
    shareStepBody: (hashtag) =>
      `Comparteix des de ChessDrop i tria aquesta promoció. Afegim @ChessDrop, ${hashtag} i l'enllaç a la teva partida.`,
    enteredStepTitle: "Fet!",
    enteredStepBody:
      "Detectem automàticament les publicacions públiques vàlides.",
    manualEntryStepTitle: "Afegeix l'enllaç d'X",
    manualEntryStepBody:
      "Torna a aquesta pàgina i afegeix l'enllaç de la publicació pública per completar la participació.",
    submissionWindow: (start, end) =>
      `El termini de participació va del ${start} al ${end}.`,
    automaticDiscovery: (_hashtag, interval) =>
      `Les publicacions vàlides a X es comproven cada ${interval} minuts.`,
    manualFallback: "No hi veus la teva? Envia'n l'enllaç a continuació.",
    tweetUrl: "URL de la teva publicació a X",
    submitTweet: "Envia la publicació",
    submitted: "La teva publicació s'ha afegit.",
    signInPrompt:
      "Inicia sessió per enviar manualment una publicació que falti.",
    signIn: "Inicia sessió",
    submittedTweets: "Publicacions registrades",
    rules: "Bases de la promoció",
  },
}

export const localeDetails: Record<
  string,
  { label: string; shortLabel: string; dateLocale: string }
> = {
  en: { label: "English", shortLabel: "EN", dateLocale: "en-GB" },
  es: { label: "Español", shortLabel: "ES", dateLocale: "es-ES" },
  fr: { label: "Français", shortLabel: "FR", dateLocale: "fr-FR" },
  ca: { label: "Català", shortLabel: "CA", dateLocale: "ca-ES" },
}

export const promotionCopyFor = (locale: string) => copies[locale] ?? copies.en

export const participationFlowFor = (
  copy: PromotionCopy,
  automatic: boolean
) => ({
  entryStepTitle: automatic ? copy.enteredStepTitle : copy.manualEntryStepTitle,
  entryStepBody: automatic ? copy.enteredStepBody : copy.manualEntryStepBody,
})

export const localeDetailFor = (locale: string) =>
  localeDetails[locale] ?? {
    label: locale.toUpperCase(),
    shortLabel: locale.toUpperCase(),
    dateLocale: locale,
  }
